import React, { useMemo } from 'react';
import { Mafs, Circle, Text, Theme } from 'mafs';

export const VennDiagram = ({ config = {}, variables = {} }) => {
    // --- 1) Config defaults / safety ---
    const sets = Array.isArray(config.sets) ? config.sets : [];

    const shadeModeRaw = config.shadeMode ?? 'INTERSECTION';
    const shadeMode = String(shadeModeRaw).trim().toUpperCase() || 'INTERSECTION';
    const shadeSetId = config.shadeSetId ?? null;

    // Layout constants
    const viewBox = { x: [-6, 6], y: [-4, 4] };
    const radius = 3;
    const separation = 2.5;
    const centerA = [-separation / 2, 0];
    const centerB = [separation / 2, 0];

    // --- 2) Helpers ---
    const toNumber = (v, fallback = 0) => {
        if (v == null) return fallback;
        const n = parseFloat(String(v));
        return Number.isFinite(n) ? n : fallback;
    };

    const normalizeSet = (set, fallbackId, fallbackLabel) => {
        const id = String(set?.id ?? '').trim() || fallbackId;
        const label = String(set?.label ?? '').trim() || fallbackLabel;
        return { ...set, id, label };
    };

    // Prefer variables keyed by set.id (stable), but allow legacy label-based keys.
    const resolveSetCount = (set) => {
        const idKey = String(set?.id ?? '').trim();
        const labelKey = String(set?.label ?? '').trim();

        // Variable overrides:
        // - countA / ACount (preferred when id is "A")
        // - count<Label> / <Label>Count (legacy)
        const v =
            (idKey ? (variables?.[`count${idKey}`] ?? variables?.[`${idKey}Count`]) : undefined) ??
            (labelKey ? (variables?.[`count${labelKey}`] ?? variables?.[`${labelKey}Count`]) : undefined);

        if (v !== undefined) return toNumber(v, 0);

        // Fallback to static config:
        // If config references a variable name string, resolve it
        const raw = set?.count;
        if (typeof raw === 'string' && variables?.[raw] !== undefined) {
            return toNumber(variables[raw], 0);
        }
        return toNumber(raw, 0);
    };

    const isSameSetId = (requested, set) => {
        if (requested == null) return false;
        const req = String(requested).trim();
        if (!req) return false;

        // Primary: match SetDefinition.id (Java contract)
        if (req === set.id) return true;

        // Backward-compatible: allow label matches too
        return req === set.label;
    };

    // SVG path: rectangle with circular holes (evenodd fill)
    const rectWithCircleHolesPath = (xMin, xMax, yMin, yMax, circles) => {
        const rect = `M ${xMin} ${yMin} H ${xMax} V ${yMax} H ${xMin} Z`;

        const holes = (circles || [])
            .map(({ cx, cy, r }) => {
                // Two arcs make a circle
                const x0 = cx - r;
                const y0 = cy;
                return `M ${x0} ${y0}
                        a ${r} ${r} 0 1 0 ${2 * r} 0
                        a ${r} ${r} 0 1 0 ${-2 * r} 0`;
            })
            .join(' ');

        return `${rect} ${holes}`;
    };

    // --- 3) Data extraction ---
    const setA = normalizeSet(sets[0], 'A', 'A');
    const setB = normalizeSet(sets[1], 'B', 'B');

    const countA = resolveSetCount(setA);
    const countB = resolveSetCount(setB);

    // Intersection extraction aligned to validator intent:
    // Priority:
    //  1) Variable override
    //  2) max(config.intersectionCount, config.intersectionItems.length)
    const configIntersectCount = toNumber(config?.intersectionCount, 0);
    const configIntersectItemsCount = Array.isArray(config?.intersectionItems) ? config.intersectionItems.length : 0;
    const syncedConfigIntersect = Math.max(configIntersectCount, configIntersectItemsCount);

    const intersectionCount = toNumber(
        variables?.intersectionCount ??
        variables?.intersection ??
        syncedConfigIntersect ??
        0,
        0
    );

    // --- 4) Region calculations ---
    const region = useMemo(() => {
        // Logical clamp: Intersection cannot be larger than the smallest set; never negative
        const i = Math.max(0, Math.min(intersectionCount, Math.min(countA, countB)));

        return {
            aOnly: Math.max(0, countA - i),
            bOnly: Math.max(0, countB - i),
            inter: i
        };
    }, [countA, countB, intersectionCount]);

    // --- 5) Shading logic (aligned with ShadeMode enum) ---
    const shouldShadeNone = shadeMode === 'NONE';
    const shouldShadeIntersection = shadeMode === 'INTERSECTION';
    const shouldShadeUnion = shadeMode === 'UNION';
    const shouldShadeSingleSet = shadeMode === 'SINGLE_SET';
    const shouldShadeComplement = shadeMode === 'COMPLEMENT';
    const shouldShadeOutside = shadeMode === 'OUTSIDE';

    const shadeSingleA = shouldShadeSingleSet && isSameSetId(shadeSetId, setA);
    const shadeSingleB = shouldShadeSingleSet && isSameSetId(shadeSetId, setB);

    // Complement target:
    // - If shadeSetId matches A or B => shade everything except that circle
    // - If missing/unknown => fall back to OUTSIDE (shade neither A nor B)
    const complementTarget =
        shouldShadeComplement && isSameSetId(shadeSetId, setA) ? 'A' :
            shouldShadeComplement && isSameSetId(shadeSetId, setB) ? 'B' :
                null;

    const xMin = viewBox.x[0], xMax = viewBox.x[1], yMin = viewBox.y[0], yMax = viewBox.y[1];

    const outsidePath = rectWithCircleHolesPath(xMin, xMax, yMin, yMax, [
        { cx: centerA[0], cy: centerA[1], r: radius },
        { cx: centerB[0], cy: centerB[1], r: radius }
    ]);

    const complementPathA = rectWithCircleHolesPath(xMin, xMax, yMin, yMax, [
        { cx: centerA[0], cy: centerA[1], r: radius }
    ]);

    const complementPathB = rectWithCircleHolesPath(xMin, xMax, yMin, yMax, [
        { cx: centerB[0], cy: centerB[1], r: radius }
    ]);

    return (
        <Mafs viewBox={viewBox} pan={false}>
            {/* Define Clip Path for Intersection geometry */}
            <defs>
                <clipPath id="circle-a-clip">
                    <circle cx={centerA[0]} cy={centerA[1]} r={radius} />
                </clipPath>
            </defs>

            {/* --- SHADING LAYERS --- */}

            {/* NONE: explicitly render nothing (kept for clarity) */}
            {!shouldShadeNone && (
                <>
                    {/* OUTSIDE: shade neither A nor B (background) */}
                    {shouldShadeOutside && (
                        <path
                            d={outsidePath}
                            fill={Theme.indigo}
                            fillOpacity={0.14}
                            fillRule="evenodd"
                            stroke="none"
                        />
                    )}

                    {/* COMPLEMENT: everything EXCEPT the chosen set */}
                    {shouldShadeComplement && (
                        <path
                            d={complementTarget === 'A' ? complementPathA : complementTarget === 'B' ? complementPathB : outsidePath}
                            fill={Theme.indigo}
                            fillOpacity={0.14}
                            fillRule="evenodd"
                            stroke="none"
                        />
                    )}

                    {/* UNION: shade both circles */}
                    {shouldShadeUnion && (
                        <>
                            <Circle center={centerA} radius={radius} color={Theme.blue} fillOpacity={0.2} strokeWidth={0} />
                            <Circle center={centerB} radius={radius} color={Theme.red} fillOpacity={0.2} strokeWidth={0} />
                        </>
                    )}

                    {/* SINGLE_SET */}
                    {(shadeSingleA || shadeSingleB) && (
                        <Circle
                            center={shadeSingleA ? centerA : centerB}
                            radius={radius}
                            color={Theme.indigo}
                            fillOpacity={0.3}
                            strokeWidth={0}
                        />
                    )}

                    {/* INTERSECTION */}
                    {shouldShadeIntersection && (
                        <g clipPath="url(#circle-a-clip)">
                            <circle cx={centerB[0]} cy={centerB[1]} r={radius} fill={Theme.indigo} fillOpacity={0.4} />
                        </g>
                    )}
                </>
            )}

            {/* --- Base Outlines (draw after shading so borders stay crisp) --- */}
            <Circle center={centerA} radius={radius} color={Theme.blue} fillOpacity={0.05} strokeWidth={2} />
            <Circle center={centerB} radius={radius} color={Theme.red} fillOpacity={0.05} strokeWidth={2} />

            {/* --- TEXT LABELS --- */}

            {/* Counts */}
            <Text x={centerA[0] - 1.2} y={0} size={24}>
                {region.aOnly}
            </Text>
            <Text x={0} y={0} size={24} weight="bold">
                {region.inter}
            </Text>
            <Text x={centerB[0] + 1.2} y={0} size={24}>
                {region.bOnly}
            </Text>

            {/* Set Titles */}
            <Text x={centerA[0]} y={radius + 0.8} weight="bold" size={18}>
                {setA.label}
            </Text>
            <Text x={centerB[0]} y={radius + 0.8} weight="bold" size={18}>
                {setB.label}
            </Text>
        </Mafs>
    );
};
